/**
 * Day-level trip helpers, kept pure so they can be tested.
 *
 *  • splitRouteByDay — the drawn road line cut into one stretch per day
 *  • addCostLabel    — what adding a stop did to the drive ("+399 km, +4h 34m")
 *  • longDayFix      — the one-tap fix for a day with too much driving
 */
import { formatDrive, formatKm } from './geo';

type LatLng = { lat: number; lng: number };
type StopLike = { id?: string; lat?: number; lng?: number; nights?: number };

/** Day each stop is reached on: 1 at the start, +1 for every night before it. */
function daysOf(stops: Pick<StopLike, 'nights'>[]): number[] {
  const out: number[] = [];
  let day = 1;
  stops.forEach((s, i) => {
    if (i > 0) day += Math.max(0, stops[i - 1].nights ?? 0);
    out.push(day);
  });
  return out;
}

function d2(a: LatLng, b: LatLng): number {
  const k = Math.cos((a.lat * Math.PI) / 180);
  const x = (a.lng - b.lng) * k;
  const y = a.lat - b.lat;
  return x * x + y * y;
}

/**
 * The road line cut at every stop you sleep at, one stretch per day, each
 * tagged with its day number. Stops are matched to the line in order (each
 * one at or after the last), so a loop that ends where it started still
 * splits right. Returns [] for a one-day trip, or when there is no line.
 */
export function splitRouteByDay(
  path: LatLng[] | null | undefined,
  stops: StopLike[]
): { day: number; path: LatLng[] }[] {
  if (!path || path.length < 2 || stops.length < 2) return [];
  const days = daysOf(stops);
  if (days[days.length - 1] <= 1) return [];
  // Vertex index nearest each stop, monotonic along the line.
  const at: number[] = [];
  let from = 0;
  for (let i = 0; i < stops.length; i += 1) {
    const s = stops[i];
    if (i === 0) {
      at.push(0);
      continue;
    }
    if (i === stops.length - 1) {
      at.push(path.length - 1);
      continue;
    }
    if (typeof s.lat !== 'number' || typeof s.lng !== 'number') {
      at.push(from);
      continue;
    }
    let best = from;
    let bestD = Infinity;
    for (let v = from; v < path.length; v += 1) {
      const d = d2(path[v], { lat: s.lat, lng: s.lng });
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    at.push(best);
    from = best;
  }
  const out: { day: number; path: LatLng[] }[] = [];
  let startV = 0;
  for (let i = 1; i < stops.length; i += 1) {
    const endsDay = i === stops.length - 1 || (stops[i].nights ?? 0) > 0;
    if (!endsDay) continue;
    const endV = at[i];
    if (endV > startV) out.push({ day: days[i], path: path.slice(startV, endV + 1) });
    startV = endV;
  }
  return out.length >= 2 ? out : [];
}

/**
 * What a change did to the whole drive: "+399 km, +4h 34m" (or "−12 km,
 * −10 min"). Under 2 km and 2 minutes either way it is "no extra driving".
 * Null when either side is unknown.
 */
export function addCostLabel(
  before: { km?: number; minutes?: number } | null | undefined,
  after: { km?: number; minutes?: number } | null | undefined
): string | null {
  if (!before || !after) return null;
  if (typeof before.km !== 'number' || typeof after.km !== 'number') return null;
  const dKm = after.km - before.km;
  const dMin =
    typeof before.minutes === 'number' && typeof after.minutes === 'number' ? after.minutes - before.minutes : null;
  if (Math.abs(dKm) < 2 && (dMin === null || Math.abs(dMin) < 2)) return 'no extra driving';
  const sign = (n: number) => (n < 0 ? '−' : '+');
  const parts = [`${sign(dKm)}${formatKm(Math.abs(dKm)) || `${Math.round(Math.abs(dKm))} km`}`];
  if (dMin !== null && Math.abs(dMin) >= 1) parts.push(`${sign(dMin)}${formatDrive(Math.abs(dMin))}`);
  return parts.join(' · ');
}

/**
 * Extra driving past which an add is a detour worth a second look. Most adds
 * cost something (a stop off the highway is +100 km on a long trip), and a
 * routine add must read as routine: the warning is kept for a real detour,
 * either a big one outright or a big share of the trip.
 */
export const HEAVY_DETOUR_KM = 200;
export const HEAVY_DETOUR_MINUTES = 180;
/** A smaller detour still counts when it adds this share of the trip… */
export const HEAVY_DETOUR_SHARE = 0.25;
/** …and is at least this many km. */
export const HEAVY_DETOUR_MIN_KM = 100;

export function addCostIsHeavy(
  before: { km?: number; minutes?: number } | null | undefined,
  after: { km?: number; minutes?: number } | null | undefined
): boolean {
  if (!before || !after || typeof before.km !== 'number' || typeof after.km !== 'number') return false;
  const dKm = after.km - before.km;
  const dMin = typeof before.minutes === 'number' && typeof after.minutes === 'number' ? after.minutes - before.minutes : 0;
  if (dKm >= HEAVY_DETOUR_KM || dMin >= HEAVY_DETOUR_MINUTES) return true;
  return dKm >= HEAVY_DETOUR_MIN_KM && before.km > 0 && dKm / before.km >= HEAVY_DETOUR_SHARE;
}

/**
 * The fix for a long day of driving (day `day`, 1-based):
 *  • 'night'  — sleep at a pass-through stop already on that day, the one
 *               nearest halfway through the day's driving;
 *  • 'insert' — no stop to sleep at: open "add a stop" on the day's longest
 *               leg (index = the stop the leg starts from).
 * Null when the day has no legs.
 */
export function longDayFix(
  stops: StopLike[],
  legs: { fromId: string; driveMinutes?: number; distanceKm?: number }[],
  day: number
): { kind: 'night'; stopIndex: number } | { kind: 'insert'; legIndex: number } | null {
  if (stops.length < 2) return null;
  const days = daysOf(stops);
  const legBy = new Map(legs.map((l) => [l.fromId, l]));
  // Legs driven that day: stop i → i+1 where you arrive at i+1 on `day`.
  const dayLegs: { i: number; min: number }[] = [];
  for (let i = 0; i < stops.length - 1; i += 1) {
    if (days[i + 1] !== day) continue;
    const l = legBy.get(stops[i].id ?? '');
    const min = typeof l?.driveMinutes === 'number' ? l.driveMinutes : typeof l?.distanceKm === 'number' ? l.distanceKm : 0;
    dayLegs.push({ i, min });
  }
  if (!dayLegs.length) return null;
  const total = dayLegs.reduce((n, l) => n + l.min, 0);
  // Pass-throughs inside the day: the arrival stop of every leg but the last.
  let run = 0;
  let best: { idx: number; off: number } | null = null;
  for (let k = 0; k < dayLegs.length - 1; k += 1) {
    run += dayLegs[k].min;
    const idx = dayLegs[k].i + 1;
    const off = Math.abs(run - total / 2);
    if (!best || off < best.off) best = { idx, off };
  }
  if (best) return { kind: 'night', stopIndex: best.idx };
  const longest = dayLegs.reduce((a, b) => (b.min > a.min ? b : a));
  return { kind: 'insert', legIndex: longest.i };
}
